import React, { useState, useEffect } from "react";
import {
  MDBIcon,
  MDBInputGroup,
  MDBInput,
  MDBCol,
  MDBRow,
  MDBSpinner,
  MDBBtn,
} from "mdb-react-ui-kit";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import getCommitteesMeetings from "../../utils/getCommitteesMeetings";
import fetchCommitteeMinutes from "../../utils/fetchCommitteeMinutes";
import fetchCommitteeAgendas from "../../utils/fetchCommitteeAgendas";
import { getDocumentUrl } from "../../utils/getDocumentUrl";

function Meetings({ id, type, legislatureOrdinal, committeeData, isActive = true }) {
  const [allMeetings, setAllMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const handleDocumentClick = async (doc) => {
    try {
      const url = await getDocumentUrl(doc.id);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error fetching document URL:", error);
    }
  };

  useEffect(() => {
    const fetchCommitteeMeetings = async () => {
      if (!isActive || hasLoaded) return;
      
      try {
        setLoading(true);

        const meetings = await getCommitteesMeetings([id], type);

        if (!Array.isArray(meetings) || meetings.length === 0) {
          setAllMeetings([]);
          return;
        }

        const meetingsWithDocs = await Promise.all(
          meetings.map(async (meeting) => {
            const committeeType = type === "standing" ? "STANDING" : "NON-STANDING";
            const committeeTypeCode = committeeData?.committeeDetails?.committeeCode?.committeeType?.code || "";
            const committeeCode = committeeData?.committeeDetails?.committeeCode?.code || "";
            const meetingDateTime = meeting.meetingTime || "";
            const chamber = committeeData?.chamber || "";
            const [minutes, agendas] = await Promise.all([
              fetchCommitteeMinutes(
                legislatureOrdinal,
                committeeType,
                committeeTypeCode,
                committeeCode,
                chamber,
                meetingDateTime
              ),
              fetchCommitteeAgendas(
                legislatureOrdinal,
                committeeType,
                committeeTypeCode,
                committeeCode,
                chamber,
                meetingDateTime
              ),
            ]);
        
            return {
              ...meeting,
              minutes: minutes || [],
              agendas: agendas || [],
            };
          })
        );
        
        setAllMeetings(meetingsWithDocs);
        
      } catch (err) {
        console.error("Error fetching committee meetings:", err);
        setAllMeetings([]);
      } finally {
        setLoading(false);
        setHasLoaded(true);
      }
    };

    fetchCommitteeMeetings();
  }, [id, type, committeeData, isActive, hasLoaded, legislatureOrdinal]);

  // If the tab isn't active, don't render anything
  if (!isActive) {
    return null;
  }

  // Show loading state only if we're actively loading
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <MDBSpinner role="status" size="lg">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  if (!allMeetings || allMeetings.length === 0) {
    return <>No meetings available.</>;
  }

  //Filter
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
  };

  // Date formatting function
  const formatDate = (value) => {
    if (!value) return "";
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return new Intl.DateTimeFormat("en-US", options).format(new Date(value));
  };

  return (
    <>
      <MDBRow className="g-1">
        <MDBCol size="lg">
          <MDBInputGroup
            className="mb-3"
            noBorder
            textBefore={<MDBIcon fas icon="search" />}
          >
            <MDBInput
              type="text"
              label="Search Meetings"
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
            />
          </MDBInputGroup>
        </MDBCol>
        {/* <MDBCol size="auto">
          <MDBDropdown className="mb-3 mb-lg-0">
            <MDBDropdownToggle>
              <MDBIcon fas icon="cloud-download-alt" /> Export
            </MDBDropdownToggle>
            <MDBDropdownMenu>
              <MDBDropdownItem link childTag="button" onClick={exportPdf}>
                Print/PDF
              </MDBDropdownItem>
              <MDBDropdownItem
                link
                childTag="button"
                onClick={() => exportCSV(false)}
              >
                Excel/CSV
              </MDBDropdownItem>
            </MDBDropdownMenu>
          </MDBDropdown>
        </MDBCol> */}
      </MDBRow>
      <DataTable
        value={allMeetings}
        resizableColumns
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={100} // Default rows per page
        rowsPerPageOptions={[25, 50, 100]}
        scrollable
        scrollHeight="500px"
        sortField="meetingTime"
        sortOrder={-1}
        removableSort
        loading={loading}
        globalFilter={globalFilterValue}
      >
        <Column
          field="meetingTime"
          header="Meeting Date & Time"
          sortable
          style={{ width: "50%" }}
          body={(rowData) => {
            const startDate = new Date(rowData.meetingTime);
            const endDate = rowData.meetingEndTime
              ? new Date(rowData.meetingEndTime)
              : null;

            const formatTime = (date) => {
              return date.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });
            };

            const formattedStart = `${startDate.toLocaleDateString(
              "en-US"
            )} ${formatTime(startDate)}`;

            if (endDate) {
              const sameDay =
                startDate.toDateString() === endDate.toDateString();
              return sameDay
                ? `${formattedStart} - ${formatTime(endDate)}`
                : `${formattedStart} - ${endDate.toLocaleDateString(
                    "en-US"
                  )} ${formatTime(endDate)}`;
            }

            return formattedStart;
          }}
        />
        <Column
          field="agendas"
          header="Agendas"
          body={(rowData) => {
            if (!Array.isArray(rowData.agendas) || rowData.agendas.length === 0) {
              return null;
            }
            return rowData.agendas.map((doc, index) => (
              <MDBBtn
                key={doc.id || index}
                tag="a"
                color="none"
                title="View Agenda"
                onClick={(e) => {
                  e.preventDefault();
                  handleDocumentClick(doc);
                }}
              >
                <MDBIcon far icon="calendar-check" /> Agenda
              </MDBBtn>
            ));
          }}
          bodyStyle={{ textAlign: "center" }}
          headerClassName="text-center"
        />
        <Column
          field="minutes"
          header="Minutes"
          body={(rowData) => {
            if (!Array.isArray(rowData.minutes) || rowData.minutes.length === 0) {
              return null;
            }
            return rowData.minutes.map((doc, index) => (
              <MDBBtn
                key={doc.id || index}
                tag="a"
                color="none"
                title="View Minutes"
                onClick={(e) => {
                  e.preventDefault();
                  handleDocumentClick(doc);
                }}
              >
                <MDBIcon far icon="clock" /> Minutes
              </MDBBtn>
            ));
          }}
          bodyStyle={{ textAlign: "center" }}
          headerClassName="text-center"
        />
        <Column field="location" header="Location" sortable />
      </DataTable>
    </>
  );
}

export default Meetings;
